import { StyledDrawer, StyledFooter } from './style';
import { BannerProps, ImageProps } from '../../../types/BannerData';
import { Input } from '../../Input';
import { useIntl } from 'react-intl';
import { Form, message } from 'antd';
import { Button } from '../../Button';
import { useForm, useWatch } from 'antd/lib/form/Form';
import { useState } from 'react';
import { Spinner } from '../../Spinner';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { FileUploader } from '../../UploadImage/FileUploader';
import { Checkbox, Icon, InputNumber } from 'ui';
import { RcFile, UploadFile } from 'antd/es/upload';
import { bannerImages, options } from 'config/config';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  bannerToEdit: BannerProps | null;
}

export const EditBannerDrawer = ({
  open,
  onClose,
  bannerToEdit,
}: DrawerProps) => {
  const [imagesData, setImagesData] = useState<UploadFile[]>([]);
  const [productsCount, setProductsCount] = useState<number>(0);
  const [banner, setBanner] = useState<BannerProps>();
  const intl = useIntl();
  const [form] = useForm();
  const autoplay = useWatch('autoplay', form);

  useQueryApiClient({
    request: {
      url: '/api/v2/products/get/count',
    },
    onSuccess: (count: number) => setProductsCount(count),
  });

  const { isLoading: isBannerLoading } = useQueryApiClient({
    request: {
      url: `/api/v2/banners/${bannerToEdit?.id}`,
    },
    onSuccess: (bannerResp: BannerProps) => storeBannerData(bannerResp),
    enabled: !!bannerToEdit?.id,
    onError: () => {},
  });

  const { appendData: updateBanner, isLoading } = useQueryApiClient({
    request: {
      url: `/api/v2/banners/update/${bannerToEdit?.id}`,
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => {
      closeDrawer();
      message.success(intl.formatMessage({ id: 'general.save_message' }));
    },
    onError: (e) => {
      const key = e?.error ?? e?.message;

      message.error(
        intl.formatMessage({
          id: key && intl.messages[key] ? key : 'validation.error',
        })
      );
    },
  });

  const { appendData: bannerFetch, isLoading: isBannerImageLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response, passOnSuccess) =>
        storeImageFiles(
          response,
          passOnSuccess.imageUrl,
          passOnSuccess.index,
          passOnSuccess.url_to
        ),
    });

  const bannersMap = {
    banner_1: { size: '1920 x 280', aspectRatio: 1920 / 280 },
    banner_2: { size: '1440 x 450', aspectRatio: 1440 / 450 },
    banner_3: { size: '1440 x 450', aspectRatio: 1440 / 450 },
    banner_4: { size: '1440 x 450', aspectRatio: 1440 / 450 },
  };

  const storeBannerData = (bannerResp: BannerProps) => {
    form.setFieldValue('title', bannerResp.title);
    form.setFieldValue('autoplay', bannerResp.autoplay);
    form.setFieldValue('autoplay_speed', bannerResp.autoplay_speed);

    setBanner(bannerResp);

    bannerResp.images.forEach((image: ImageProps, index: number) => {
      form.setFieldValue(`id_${index}`, image.id);

      form.setFieldValue(
        bannerImages[bannerResp.type][index].name,
        image.url_to
      );

      if (image.url_original) {
        bannerFetch({ path: image.url_original }, [], {
          imageUrl: image.url_original,
          index: index,
          url_to: image.url_to,
        });
      }
    });
  };

  const storeImageFiles = (
    file: Blob,
    url: string,
    index: number,
    url_to: string
  ) => {
    const fileName = url.split('/').pop() || 'unknown_file';

    const rcFile = new File([file], fileName || '', {
      type: file.type,
    }) as RcFile;

    const blobUrl = URL.createObjectURL(rcFile);
    rcFile.uid = `${Date.now()}-${Math.random()}`;

    const uploadFile: UploadFile = {
      uid: rcFile.uid,
      name: rcFile.name,
      preview: blobUrl,
      originFileObj: rcFile,
      url: URL.createObjectURL(rcFile),
    };

    let parsed = imagesData;
    parsed[index] = uploadFile;

    setImagesData(parsed);
  };

  const closeDrawer = () => {
    form.resetFields();
    setImagesData([]);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const formData = new FormData();

      if (banner?.type === 'banner_1' && imagesData.length <= 0) {
        throw Error('validation.banner_1_file_required');
      }

      formData.append('title', values.title ?? '');
      formData.append('autoplay', values.autoplay ? '1' : '0');

      if (values.autoplay) {
        formData.append('autoplay_speed', String(values.autoplay_speed ?? 0));
      }

      banner &&
        bannerImages[banner.type].forEach((_, index) => {
          if (imagesData[index]) {
            const galleryFile = imagesData[index].originFileObj;

            if (!galleryFile) return;

            const realFile = new File([galleryFile], galleryFile.name, {
              type: galleryFile.type,
              lastModified: galleryFile.lastModified || Date.now(),
            });
            formData.append(`file[${index}]`, realFile, realFile.name);
          }

          const id = form.getFieldValue(`id_${index}`);
          const url_to = form.getFieldValue(`url_${index}`);
          formData.append(`id[${index}]`, id ?? '');
          formData.append(`url_to[${index}]`, url_to ?? '');
        });

      updateBanner(formData);
    } catch (e: any) {
      const err = e as Error;
      if (err.message === 'validation.banner_1_file_required') {
        message.error(intl.formatMessage({ id: err.message }));
      } else {
        message.error(intl.formatMessage({ id: 'validation.error' }));
      }
    }
  };

  const TotalProducts = () => {
    return (
      <div className={'product-count-wrapper'}>
        <div className={'productCount'}>
          <span>
            {intl.formatMessage({ id: 'banners.total_product_count' })}
          </span>
          <h1>{productsCount}</h1>
        </div>
      </div>
    );
  };

  const Footer = () => {
    return (
      <StyledFooter>
        <Button
          label={intl.formatMessage({ id: 'general.submit' })}
          htmlType={'submit'}
          onClick={handleSubmit}
        />
      </StyledFooter>
    );
  };

  return (
    <StyledDrawer
      open={open}
      closable
      onClose={closeDrawer}
      footer={<Footer />}
      width={1000}
      title={intl.formatMessage({ id: 'general.edit_banner' })}
      closeIcon={<Icon name={'global-close-icon'} />}
      destroyOnClose={true}
    >
      <Spinner spinning={isLoading || isBannerLoading || isBannerImageLoading}>
        <Form layout={'vertical'} form={form} name={'banner_edit'}>
          <Input
            value={banner?.title}
            label={intl.formatMessage({ id: 'banners.button_name' })}
            name={'title'}
          />
          {banner &&
            bannerImages[banner.type].map((field: any) => (
              <Input
                key={field.name}
                type="text"
                label={intl.formatMessage({ id: 'banners.url' })}
                name={field.name}
              />
            ))}
          <p className={'banner-drawer-url-warning'}>
            {intl.formatMessage({ id: 'banners.add_https_to_link' })}
          </p>
          <TotalProducts />
          {imagesData.length > 1 && (
            <Checkbox
              name={'autoplay'}
              label={intl.formatMessage({ id: 'banners.autolpay' })}
              className={'edit-banner-autoplay'}
            />
          )}
          {imagesData.length > 1 && autoplay && (
            <InputNumber
              name={'autoplay_speed'}
              label={intl.formatMessage({ id: 'banners.autoplay_speed' })}
              min={1}
            />
          )}
          {banner?.type && (
            <FileUploader
              onFileListChange={setImagesData}
              fileList={imagesData}
              multiple={banner?.type !== 'banner_1'}
              isCrop={true}
              requirement={{
                maxImageSize: banner?.type
                  ? bannersMap[banner?.type].size
                  : options.images.maxImageSize,
                multiple: banner?.type !== 'banner_1',
              }}
              aspectRatio={bannersMap[banner?.type]?.aspectRatio}
              showDefault={false}
            />
          )}
        </Form>
      </Spinner>
    </StyledDrawer>
  );
};
